import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { Form, Popover, DatePicker, TimeRangePickerProps } from 'antd';

// Shared UI Library Components
import { CustomIcon, EIconName, SpinElement } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './transactions.scss';

// Components
import {
  TotalInfo,
  TransactionsTable, TransactionsList
} from '../../shared/components/transactions';
import { ConsultationType } from '../../shared/components/transactions/consultation-type/consultation-type';

// Store entities
import {
  changeTransactionsRecording,
  clearTransactions,
  saveTransactionsSetting
} from '../../store/transactions/transactions.thunks';
import { getTransactionsState } from '../../store/transactions/transactions.selectors';
import { getCompanyState } from '../../store/company/company.selectors';
import { getLastMessageData } from '../../store/socket/socket.selectors';

// Models
import { ITransactionSettings } from '../../interfaces/transactions.interfaces';
import { EWebSocketTypes } from '../../enums/web-socket-types.enums';

type TTransactionsProps = {};

export const Transactions: FC<TTransactionsProps> = (
  {}: TTransactionsProps
) => {
  const [form] = Form.useForm<{
    dates: [min: string, max: string];
    types?: string[];
  }>();

  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const {
    transactionsList,
    settings: storeSettings,
    loading,
    total,
    total_all
  } = useSelector(getTransactionsState);
  const { company } = useSelector(getCompanyState);
  const lastMessage = useSelector(getLastMessageData);

  const dispatch = useDispatch<any>();

  const hasFiltersChanges = storeSettings.show_messages === false ||
    storeSettings.show_calls === false ||
    storeSettings.show_audio_calls === false ||
    storeSettings.show_conversations === false;

  const loadTransactionsData = (settings: ITransactionSettings, isFirstLoad: boolean = false): void => {
    dispatch(saveTransactionsSetting({ settings, isFirstLoad }));
  }

  const handleOpenChange = (open: boolean): void => {
    if (!total_all) return;
    setOpenFilters(open);
  };

  const disabledDate: TimeRangePickerProps['disabledDate'] = (current): boolean => {
    // Can not select months before company created_at
    return current < dayjs(company?.created_at).startOf('day') || current > dayjs().endOf('day');
  };

  const onValuesChange = (_: { [key: string]: string | string[] }, {
    dates, types
  }: {
    dates: [min: string, max: string]
    types?: string[];
  }): void => {

    let settings: ITransactionSettings = { ...storeSettings };

    if (dates) {
      settings.date_from = dayjs(dates[0]).format('DD MMM YYYY');
      settings.date_to = dayjs(dates[1]).format('DD MMM YYYY');
    }

    if (dates === null) {
      settings.date_from = undefined;
      settings.date_to = undefined;
    }

    if (types) {
      settings = {
        ...settings,
        show_audio_calls: types.includes('show_audio_calls'),
        show_calls: types.includes('show_calls'),
        show_messages: types.includes('show_messages'),
        show_conversations: types.includes('show_conversations'),
      }
    }

    loadTransactionsData(settings);
  };

  useEffect(() => {
    loadTransactionsData(storeSettings, true);

    return () => {
      dispatch(clearTransactions());
    };
  }, []);

  useEffect(() => {
    if (lastMessage?.type === EWebSocketTypes.CallRecordingAvailable) {
      dispatch(changeTransactionsRecording({ transaction_id: lastMessage.data.transaction_id, has_recording_till: lastMessage.data.has_recording_till }));
    }
  }, [lastMessage]);

  return (
    <Form className="transactions" form={ form }
          onValuesChange={ onValuesChange }>
      <div className="transactions--header">
        <div className="header-title">
          Transactions
        </div>
        <div className="header-dates">
          <Form.Item name={ 'dates' }>
            <DatePicker.RangePicker
              className="date-range-element"
              format={ "DD MMM YYYY" }
              allowEmpty={[true, true]}
              allowClear={ { clearIcon: <CustomIcon name={ EIconName.Cancel }/> } }
              disabled={ !total_all && !storeSettings.date_from && !storeSettings.date_to }
              disabledDate={ disabledDate }
              suffixIcon={ <CustomIcon name={ EIconName.CalendarToday }/> }
            />
          </Form.Item>
        </div>
        <div
          className={ `header-filter-action ${ !total_all ? 'disabled-action' : '' } ${ hasFiltersChanges ? 'has-filters' : '' }` }>
          <Popover
            content={ <ConsultationType/> }
            trigger="click"
            placement={ 'bottomLeft' }
            open={ openFilters }
            onOpenChange={ handleOpenChange }
          >
            <CustomIcon name={ EIconName.Tune }/>
          </Popover>
        </div>
      </div>

      {
        loading &&
        <div className={ `transactions--loader transactions--loader-${ loading ? 'show' : 'hide' }` }>
          <SpinElement fullHeight={ true }/>
        </div>
      }

      {
        <div className="transactions--body">
          <div className="body-aside">
            <TotalInfo/>
          </div>
          <div className="body-content">
            <div className="body-content--desktop">
              <TransactionsTable transactionsList={ transactionsList }
                                 total={ total }
                                 transactionsSettings={ storeSettings }/>
            </div>

            <div className="body-content--mobile">
              <TransactionsList transactionsList={ transactionsList }
                                loading={ loading }
                                total={ total }
                                transactionsSettings={ storeSettings }/>
            </div>
          </div>
        </div>
      }

    </Form>
  );
}
